<template>
    <v-container fluid>   

        <!-- Headline with Invoice Document number and Recipient -->
        <div v-if="$i18n.locale!='ja'">
            <v-col>
                <v-row class="pl-4 headline">
                    {{ $t(getLangOfType(item.type)) }} {{ item.number }} 
                    {{ $t('lang_GENERAL_CAPTION_EDIT_PAGE_TITLE_FOR') }} 
                    {{ item.recipientName }}
                </v-row>
            </v-col>
        </div>
        <div v-else>
            <v-col>
                <v-row class="pl-4 headline">
                    {{ item.recipientName }} 
                    {{ $t('lang_GENERAL_CAPTION_EDIT_PAGE_TITLE_FOR') }} 
                    {{ $t(getLangOfType(item.type)) }} {{ item.number }}
                </v-row>
            </v-col>
        </div>

        <v-row class="pl-4 pr-4">

            <!-- Column with Recipient -->  
            <v-col>
                <v-card elevation="0">
                    <v-card-text>
                        <span v-if="item.recipientFullAddress !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_RECIPIENT') }}</div> 
                            <div v-html="item.recipientFullAddress"></div>
                            <div v-if="item.vatId != null">
                                <div class="read-only-caption"><br />{{ $t('lang_ERP_INVOICES_CAPTION_RECIPIENT_VATID') }}</div> 
                                {{ item.vatId }}
                            </div>
                            <div v-if="item.language != null">
                                <div class="read-only-caption"><br />{{ $t('lang_ERP_INVOICES_CAPTION_LANGUAGE') }}</div> 
                                {{ $t(getLangOfLanguage(item.language))  }}
                            </div>
                        </span>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Column with IDs -->
            <v-col>
                <v-card elevation="0">
                    <v-card-text>
                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_TYPE') }}</div>
                        <div>{{ $t(getLangOfType(item.type)) }}</div>

                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_STATUS') }}</div>
                        <div>{{ $t(getLangOfStatus(item.status)) }}</div>

                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_SALES_TYPES') }}</div>
                        <div>{{ $t(getLangOfSalesType(item.salesType)) }}</div>
                        
                        <span v-if="item.number !== '' && item.number !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_NUMBER') }}</div>
                            <div>{{ item.number }}</div>
                        </span>

                        <div class="read-only-caption">{{ $t('lang_CAPTION_ALVID') }}</div>
                        <div>{{ item.alvId }}</div>

                        <div v-if="item.transactionNumber !== null && item.transactionNumber !== undefined" class="read-only-caption">{{ $t('lang_ERP_CAPTION_TRANSACTION_NUMBER') }}</div>
                        <div>{{ item.transactionNumber }}</div>
                        
                        <span v-if="item.linkedDocument !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_LINKED_DOC') }}</div>
                            <div>{{ $t(getLangOfLinkedDocumentType(item.linkedDocument.type, 'text')) }}</div>
                            <router-link :to="`/erp/shipping/${item.linkedDocument.alvId}`">{{ item.linkedDocument.alvId }}</router-link>
                        </span>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Column with Amounts -->
            <v-col>
                <v-card elevation="0">
                    <v-card-text>
                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_SUM_NET') + ' ' + item.currency }}</div>
                        <div>{{ $n(item.sumNet, 'number') }}</div>
                        
                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_SUM_GROSS') + ' ' + item.currency }}</div>
                        <div>{{ $n(item.sumGross, 'number') }}</div>

                        <div class="read-only-caption">{{ $t('lang_GENERAL_CAPTION_CONVERSION_FACTOR_ABBREVIATION') + ' ' + item.currency }}</div>
                        <div>{{ $n(item.conversionFactor, 'number') }}</div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Column with Dates and Terms -->
            <v-col>
                <v-card elevation="0">
                    <v-card-text>
                        <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_DATE') }}</div>
                        <div>{{ $d(new Date(item.date), 'alvDateFormat') }}</div>
                        
                        <span v-if="item.dueDate !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_DUEDATE') }}</div>
                            <div>{{ $d(new Date(item.dueDate), 'alvDateFormat')  }}</div>
                        </span>
                        <span v-if="item.term !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_PAYMENT_TARGET') }}</div>
                            <div><span>{{ item.term }} {{ $t('lang_CAPTION_DAYS') }}</span></div>
                        </span>

                        <span v-if="item.paidDate !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_PAIDDATE') }}</div>
                            <div>{{ $d(new Date(item.paidDate), 'alvDateFormat') }}</div>
                        </span>

                        <span v-if="item.paymentMethod !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_PAYMENT_METHOD') }}</div>
                            <div>{{ $t(getLangOfPaymentMethod(item.paymentMethod)) }}</div>
                        </span>

                        <span v-if="item.paymentMethodDetails !== null">
                            <div class="read-only-caption">{{ $t('lang_ERP_INVOICES_CAPTION_PAYMENT_METHOD_DETAILS') }}</div>
                            <div>{{ item.paymentMethodDetails }}</div>
                        </span>

                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Table with Invoice Rows -->
        <v-row v-if="item.invoiceRows != null">
            <v-col class="ml-4 mr-4">
                <v-expansion-panels multiple focusable tile flat :value="[0]">
                    <InvoiceRowsReadOnlyPanel :alvId="item.alvId"/>
                </v-expansion-panels>
            </v-col>
        </v-row>
        
    </v-container>
</template>

<script>

import InvoiceRowsReadOnlyPanel from './_panels/1_invoicerows/InvoiceRowsReadOnlyPanel'
import  {   DropdownErpInvoicesPaymentMethod,
            DropdownErpInvoicesType,
            DropdownErpInvoicesStatus,
            DropdownGenericLabelSalesTypes,
            DropdownErpInvoicesSalesTypesPublishing,
            DropdownErpLinkedDocumentType,
            DropdownErpInvoicesLanguages,
        } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "InvoicesReadOnly",
    components: {
        InvoiceRowsReadOnlyPanel
    },
    props: {
        itemId: {
            type: Number
        }
    },
    computed: {
        item() {
            let item = this.$store.getters.getExpandedInvoices.find(element => {
                return element.id === this.itemId
            })
            return item
        }
    },
    methods: {
        getLangOfPaymentMethod(selection) {
            if (selection) {
                return DropdownErpInvoicesPaymentMethod.find(obj => {
                    return obj.value == selection
                }).text
            }
        },
        getLangOfType(selection) {
            return DropdownErpInvoicesType.find(obj => {
                return obj.value == selection
            }).text
        },
        getLangOfStatus(selection) {
            return DropdownErpInvoicesStatus.find(obj => {
                return obj.value == selection
            }).text
        },
        getLangOfLanguage(selection) {
            return DropdownErpInvoicesLanguages.find(obj => {
                return obj.value == selection
            }).text
        },
        getLangOfSalesType(selection) {
            if (this.$store.getters.getSelectedOverAllCompany.context == 2) { // Label company selected
                return DropdownGenericLabelSalesTypes.find(obj => {
                    return obj.value == selection
                }).text
            } else { // Publishing company
                return DropdownErpInvoicesSalesTypesPublishing.find(obj => {
                    return obj.value == selection
                }).text              
            }
        },
        getLangOfLinkedDocumentType(selection, type = "text") {
            return DropdownErpLinkedDocumentType.find(obj => {
                return obj.value == selection
            })[type]
        },
    }
}
</script>